// Terrain geometry in world units, built once per map (02-SYMBOLOGY.md §8).
// The drawing code (layers/terrain.ts) only strokes and fills what this produces,
// so this module is plain data in, plain data out, and unit-tested.
import type { Cover, MapData } from '../sim/types';
import {
  DIR_SIDE,
  SIDE_DIR,
  axialToOffset,
  centerOf,
  centerOfOffset,
  cornerFromKey,
  cornerKey,
  dirBetween,
  hexAtPoint,
  hash01,
  neighborOffset,
  type Point,
} from './hexgeom';

export type Polyline = { pts: Point[]; closed: boolean };

export type Contour = Polyline & { level: number; index: boolean };

export type TerrainGeometry = {
  width: number;
  height: number;
  /** Outline of the map (union of its hexes), inside on the right. */
  outline: Point[];
  contours: Contour[];
  /** Region outlines (corner loops, inside on the right; holes included). */
  woods: Point[][];
  water: Point[][];
  /** Hex centers per cover, for stipple, buildings and tufts. */
  centers: Record<Cover, { x: number; y: number; col: number; row: number }[]>;
  rivers: Polyline[];
  streams: Polyline[];
  roads: Polyline[];
  /** Bridges: crossing point and road direction (unit vector). */
  bridges: { at: Point; dir: Point }[];
  /** Unit vectors from a hex center along each road leaving it, keyed "col,row". */
  roadDirs: Map<string, Point[]>;
  /** Elevation level of the hex under a world point (0 off the map). */
  elevAt: (p: Point) => number;
};

// ---------- region outlines ----------

/**
 * Trace the outline loops of a set of hexes. `inside(col,row)` is queried for
 * col ∈ [c0,c1], row ∈ [r0,r1]; anything outside that range counts as outside.
 * Loops run clockwise on screen with the region on the right, so "outward" is to the left.
 * At any corner exactly zero or two outline sides meet, so chaining is unambiguous.
 */
export function traceOutlines(
  inside: (col: number, row: number) => boolean,
  c0: number,
  c1: number,
  r0: number,
  r1: number,
): Point[][] {
  const isIn = (c: number, r: number): boolean =>
    c >= c0 && c <= c1 && r >= r0 && r <= r1 && inside(c, r);
  const next = new Map<string, string>();
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      if (!isIn(c, r)) continue;
      for (let side = 0; side < 6; side++) {
        const [nc, nr] = neighborOffset(c, r, SIDE_DIR[side] as number);
        if (isIn(nc, nr)) continue;
        next.set(cornerKey(c, r, side), cornerKey(c, r, (side + 1) % 6));
      }
    }
  }
  const loops: Point[][] = [];
  const done = new Set<string>();
  // Sorted start keys keep the output order stable.
  for (const start of [...next.keys()].sort()) {
    if (done.has(start)) continue;
    const loop: Point[] = [];
    let k: string | undefined = start;
    while (k !== undefined && !done.has(k)) {
      done.add(k);
      loop.push(cornerFromKey(k));
      k = next.get(k);
    }
    loops.push(loop);
  }
  return loops;
}

// ---------- contours (marching triangles on hex centers) ----------

type HeightAt = (col: number, row: number) => number;

/**
 * Contour lines between hex-center heights. Hex centers form a triangular lattice, so this is
 * the hex equivalent of marching squares: each triangle of three mutual neighbors contributes
 * at most one segment per level. Level k is drawn at height k − 0.5 (between k−1 and k).
 * Heights are extended one ring past the map edge (copying the nearest edge hex) so lines run
 * to the border; the drawing code clips to the map outline.
 */
export function buildContours(
  width: number,
  height: number,
  heightAt: HeightAt,
  smoothPasses = 40,
): Contour[] {
  const clampH = (c: number, r: number): number =>
    heightAt(Math.min(width - 1, Math.max(0, c)), Math.min(height - 1, Math.max(0, r)));
  const ring = 2;
  const vKey = (c: number, r: number): string => `${c},${r}`;
  const inRange = (c: number, r: number): boolean =>
    c >= -ring && c < width + ring && r >= -ring && r < height + ring;
  let maxH = 0;
  for (let r = 0; r < height; r++)
    for (let c = 0; c < width; c++) maxH = Math.max(maxH, heightAt(c, r));

  const out: Contour[] = [];
  for (let level = 1; level <= maxH; level++) {
    const t = level - 0.5;
    // Segment endpoints are identified by the lattice edge they sit on.
    const segs: [string, string][] = [];
    const pos = new Map<string, Point>();
    const crossing = (a: [number, number], b: [number, number]): string => {
      const ka = vKey(a[0], a[1]);
      const kb = vKey(b[0], b[1]);
      const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
      if (!pos.has(key)) {
        const ha = clampH(a[0], a[1]);
        const hb = clampH(b[0], b[1]);
        const pa = centerOfOffset(a[0], a[1]);
        const pb = centerOfOffset(b[0], b[1]);
        const f = (t - ha) / (hb - ha);
        pos.set(key, { x: pa.x + (pb.x - pa.x) * f, y: pa.y + (pb.y - pa.y) * f });
      }
      return key;
    };
    for (let r = -ring; r < height + ring; r++) {
      for (let c = -ring; c < width + ring; c++) {
        // Two triangles per hex: (self, SE, S) and (self, NE, SE).
        const se = neighborOffset(c, r, 2);
        const s = neighborOffset(c, r, 3);
        const ne = neighborOffset(c, r, 1);
        for (const tri of [
          [[c, r], se, s],
          [[c, r], ne, se],
        ] as [number, number][][]) {
          if (!tri.every(([x, y]) => inRange(x, y))) continue;
          const above = tri.map(([x, y]) => clampH(x, y) > t);
          const n = above.filter(Boolean).length;
          if (n === 0 || n === 3) continue;
          // The odd vertex out is alone on its side of the level.
          const odd = n === 1 ? above.indexOf(true) : above.indexOf(false);
          const o = tri[odd] as [number, number];
          const p = tri[(odd + 1) % 3] as [number, number];
          const q = tri[(odd + 2) % 3] as [number, number];
          segs.push([crossing(o, p), crossing(o, q)]);
        }
      }
    }
    for (const chain of chainSegments(segs)) {
      const pts = chain.keys.map((k) => pos.get(k) as Point);
      const smoothed = taubin(resample(pts, chain.closed, 0.25), chain.closed, smoothPasses);
      out.push({ pts: smoothed, closed: chain.closed, level, index: level % 5 === 0 });
    }
  }
  return out;
}

/** Join segments that share endpoint keys into polylines. Deterministic order. */
export function chainSegments(segs: [string, string][]): { keys: string[]; closed: boolean }[] {
  const adj = new Map<string, number[]>();
  segs.forEach(([a, b], i) => {
    for (const k of [a, b]) {
      const list = adj.get(k);
      if (list) list.push(i);
      else adj.set(k, [i]);
    }
  });
  const used = new Array<boolean>(segs.length).fill(false);
  const result: { keys: string[]; closed: boolean }[] = [];
  const walk = (startKey: string): { keys: string[]; closed: boolean } => {
    const keys = [startKey];
    let cur = startKey;
    for (;;) {
      const nextSeg = (adj.get(cur) ?? []).find((i) => !used[i]);
      if (nextSeg === undefined) break;
      used[nextSeg] = true;
      const [a, b] = segs[nextSeg] as [string, string];
      cur = a === cur ? b : a;
      if (cur === startKey) return { keys, closed: true };
      keys.push(cur);
    }
    return { keys, closed: false };
  };
  const ends = [...adj.keys()].filter((k) => (adj.get(k) as number[]).length !== 2).sort();
  for (const k of ends) {
    while ((adj.get(k) as number[]).some((i) => !used[i])) result.push(walk(k));
  }
  const rest = [...adj.keys()].sort();
  for (const k of rest) {
    while ((adj.get(k) as number[]).some((i) => !used[i])) result.push(walk(k));
  }
  return result;
}

/** Chaikin corner cutting. Open lines keep their end points. */
export function chaikin(pts: Point[], closed: boolean, passes: number): Point[] {
  let cur = pts;
  for (let p = 0; p < passes; p++) {
    if (cur.length < 3) return cur;
    const next: Point[] = [];
    const n = cur.length;
    const segCount = closed ? n : n - 1;
    if (!closed) next.push(cur[0] as Point);
    for (let i = 0; i < segCount; i++) {
      const a = cur[i] as Point;
      const b = cur[(i + 1) % n] as Point;
      const first = !closed && i === 0;
      const last = !closed && i === segCount - 1;
      if (!first) next.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      if (!last) next.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    if (!closed) next.push(cur[n - 1] as Point);
    cur = next;
  }
  return cur;
}

/**
 * Taubin smoothing (alternating shrink and inflate steps): rounds the hexagonal corners that
 * hex-center contours inherit, without shrinking small closed loops the way plain averaging does.
 * Open lines keep their end points.
 */
export function taubin(pts: Point[], closed: boolean, passes: number): Point[] {
  const n = pts.length;
  if (n < 3) return pts;
  let cur = pts.map((p) => ({ ...p }));
  for (let pass = 0; pass < passes * 2; pass++) {
    const k = pass % 2 === 0 ? 0.5 : -0.53;
    cur = cur.map((p, i) => {
      if (!closed && (i === 0 || i === n - 1)) return p;
      const a = cur[(i - 1 + n) % n] as Point;
      const b = cur[(i + 1) % n] as Point;
      return { x: p.x + k * ((a.x + b.x) / 2 - p.x), y: p.y + k * ((a.y + b.y) / 2 - p.y) };
    });
  }
  return cur;
}

/** Points every `spacing` world units along a polyline (by arc length). */
export function resample(pts: Point[], closed: boolean, spacing: number): Point[] {
  const src = closed ? [...pts, pts[0] as Point] : pts;
  const out: Point[] = [];
  if (src.length === 0) return out;
  let total = 0;
  for (let i = 1; i < src.length; i++) {
    total += Math.hypot(
      (src[i] as Point).x - (src[i - 1] as Point).x,
      (src[i] as Point).y - (src[i - 1] as Point).y,
    );
  }
  const n = Math.max(closed ? 3 : 1, Math.round(total / spacing));
  const step = total / n;
  let seg = 1;
  let segStart = 0;
  for (let k = 0; k < (closed ? n : n + 1); k++) {
    const d = k * step;
    while (seg < src.length - 1) {
      const a = src[seg - 1] as Point;
      const b = src[seg] as Point;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (segStart + len >= d) break;
      segStart += len;
      seg++;
    }
    const a = src[seg - 1] as Point;
    const b = src[seg] as Point;
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const f = Math.min(1, Math.max(0, (d - segStart) / len));
    out.push({ x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f });
  }
  return out;
}

// ---------- edges: rivers, streams, roads ----------

/** Rivers and streams run along hex sides; join consecutive sides into polylines. */
function sideLines(map: MapData, kinds: string[]): Polyline[] {
  const segs: [string, string][] = [];
  const seen = new Set<string>();
  for (const e of map.edges) {
    if (!kinds.includes(e.kind)) continue;
    const d = dirBetween(e.a, e.b);
    if (d < 0) continue;
    const side = DIR_SIDE[d] as number;
    const [c, r] = axialToOffset(e.a);
    const k1 = cornerKey(c, r, side);
    const k2 = cornerKey(c, r, (side + 1) % 6);
    const id = k1 < k2 ? `${k1}|${k2}` : `${k2}|${k1}`;
    if (seen.has(id)) continue;
    seen.add(id);
    segs.push([k1, k2]);
  }
  return chainSegments(segs).map((ch) => ({
    pts: chaikin(ch.keys.map(cornerFromKey), ch.closed, 2),
    closed: ch.closed,
  }));
}

/** Roads run center to center; bridges carry a road too. Dead ends at the map edge extend off it. */
function roadLines(map: MapData): Polyline[] {
  const segs: [string, string][] = [];
  const seen = new Set<string>();
  const key = (q: number, r: number): string => `${q},${r}`;
  for (const e of map.edges) {
    if (e.kind !== 'road' && e.kind !== 'bridge') continue;
    const ka = key(e.a.q, e.a.r);
    const kb = key(e.b.q, e.b.r);
    const id = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
    if (seen.has(id)) continue;
    seen.add(id);
    segs.push([ka, kb]);
  }
  const onBorder = (q: number, r: number): boolean => {
    const [c, row] = axialToOffset({ q, r });
    return c === 0 || row === 0 || c === map.width - 1 || row === map.height - 1;
  };
  const toPt = (k: string): Point => {
    const [q, r] = k.split(',').map(Number) as [number, number];
    return centerOf({ q, r });
  };
  return chainSegments(segs).map((ch) => {
    const pts = ch.keys.map(toPt);
    if (!ch.closed && pts.length >= 2) {
      // Extend dead ends on the border off the map so roads read as continuing.
      const ext = (endKey: string, end: Point, prev: Point): Point | null => {
        const [q, r] = endKey.split(',').map(Number) as [number, number];
        if (!onBorder(q, r)) return null;
        return { x: end.x + (end.x - prev.x) * 1.2, y: end.y + (end.y - prev.y) * 1.2 };
      };
      const head = ext(ch.keys[0] as string, pts[0] as Point, pts[1] as Point);
      const tail = ext(
        ch.keys[ch.keys.length - 1] as string,
        pts[pts.length - 1] as Point,
        pts[pts.length - 2] as Point,
      );
      if (head) pts.unshift(head);
      if (tail) pts.push(tail);
    }
    return { pts: chaikin(pts, ch.closed, 2), closed: ch.closed };
  });
}

// ---------- everything ----------

export function buildTerrain(map: MapData): TerrainGeometry {
  const { width, height, cells } = map;
  const cell = (c: number, r: number) => cells[r * width + c] as (typeof cells)[number];
  const clampCell = (c: number, r: number) =>
    cell(Math.min(width - 1, Math.max(0, c)), Math.min(height - 1, Math.max(0, r)));
  // Regions extend past the edge so outlines close off-map (then get clipped).
  const ring = 2;
  const region = (cover: Cover): Point[][] =>
    traceOutlines(
      (c, r) => clampCell(c, r).cover === cover,
      -ring,
      width - 1 + ring,
      -ring,
      height - 1 + ring,
    );

  const centers: TerrainGeometry['centers'] = {
    open: [],
    woods: [],
    urban: [],
    swamp: [],
    water: [],
  };
  for (let r = 0; r < height; r++) {
    for (let c = 0; c < width; c++) {
      const p = centerOfOffset(c, r);
      centers[cell(c, r).cover].push({ x: p.x, y: p.y, col: c, row: r });
    }
  }

  const bridges = map.edges
    .filter((e) => e.kind === 'bridge')
    .map((e) => {
      const a = centerOf(e.a);
      const b = centerOf(e.b);
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      return {
        at: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        dir: { x: (b.x - a.x) / len, y: (b.y - a.y) / len },
      };
    });

  const roadDirs = new Map<string, Point[]>();
  for (const e of map.edges) {
    if (e.kind !== 'road' && e.kind !== 'bridge') continue;
    for (const [from, to] of [
      [e.a, e.b],
      [e.b, e.a],
    ] as const) {
      const a = centerOf(from);
      const b = centerOf(to);
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      const key = axialToOffset(from).join(',');
      const list = roadDirs.get(key) ?? [];
      list.push({ x: (b.x - a.x) / len, y: (b.y - a.y) / len });
      roadDirs.set(key, list);
    }
  }
  const elevAt = (p: Point): number => {
    const [c, r] = axialToOffset(hexAtPoint(p));
    if (c < 0 || r < 0 || c >= width || r >= height) return 0;
    return cell(c, r).elev;
  };

  return {
    width,
    height,
    roadDirs,
    elevAt,
    outline: traceOutlines(() => true, 0, width - 1, 0, height - 1)[0] ?? [],
    contours: buildContours(width, height, (c, r) => cell(c, r).elev),
    woods: region('woods'),
    water: region('water'),
    centers,
    rivers: sideLines(map, ['river', 'bridge']),
    streams: sideLines(map, ['stream']),
    roads: roadLines(map),
    bridges,
  };
}

export { hash01 };
